import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  BarChart3, CalendarCheck, CheckCircle2, Clock, CreditCard, Download, ImagePlus, KeyRound,
  LogOut, Mail, PackageCheck, Pencil, Plus, Trash2, Users, X,
} from "lucide-react";
import { api, fmtEUR, fmtXOF } from "../lib/api";
import { LogoMark } from "../components/Logo";

const ORDER_STATUS = [
  { key: "en_attente", label: "En attente" },
  { key: "confirmee", label: "Confirmée" },
  { key: "livree", label: "Livrée" },
  { key: "annulee", label: "Annulée" },
];

const RDV_STATUS = [
  { key: "demandee", label: "Demandée" },
  { key: "confirmee", label: "Confirmée" },
  { key: "honoree", label: "Honorée" },
  { key: "annulee", label: "Annulée" },
];

const CATEGORIES = [
  { key: "homme", label: "Homme" },
  { key: "femme", label: "Femme" },
  { key: "couple", label: "Ensemble Couple" },
  { key: "enfants", label: "Enfants" },
  { key: "maroquinerie", label: "Maroquinerie & Accessoires" },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const LoginScreen = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
      toast.success(`Bienvenue, ${data.name}`);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Connexion impossible.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="admin-login-page" className="flex min-h-screen items-center justify-center px-6 pt-24">
      <form data-testid="admin-login-form" onSubmit={submit} className="gold-frame w-full max-w-sm border border-ligne bg-card2 p-10">
        <div className="flex flex-col items-center gap-3 text-center">
          <LogoMark size={46} />
          <p className="font-serif text-2xl tracking-[0.15em] text-ivoire">ESPACE DIRECTION</p>
          <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-or">Accès réservé — B.KOSS</p>
        </div>
        <div className="mt-8 space-y-5">
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">E-mail</label>
            <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="direction@bkoss.net" className="w-full border border-ligne bg-elevated px-4 py-3.5 text-sm text-ivoire focus:border-or" />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">Mot de passe</label>
            <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••••" className="w-full border border-ligne bg-elevated px-4 py-3.5 text-sm text-ivoire focus:border-or" />
          </div>
          <button data-testid="admin-login-button" type="submit" disabled={busy} className="w-full bg-or py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-noir transition-all hover:bg-orclair disabled:opacity-50">
            {busy ? "Connexion…" : "Entrer"}
          </button>
        </div>
      </form>
    </main>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div data-testid={testid} className="gold-frame border border-ligne bg-card2 p-6">
    <Icon size={20} strokeWidth={1.4} className="text-or" />
    <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.25em] text-argile">{label}</p>
    <p className="mt-2 font-serif text-2xl text-ivoire">{value}</p>
    {sub && <p className="mt-1 text-[11px] text-argile">{sub}</p>}
  </div>
);

const StatusPill = ({ status }) => (
  <span className="inline-block border border-or/40 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-or">
    {ORDER_STATUS.concat(RDV_STATUS).find((s) => s.key === status)?.label || status}
  </span>
);

const EMPTY_PRODUCT = { name: "", category: "homme", sub: "", description: "", fabric: "", price_xof: "", sizes: "S, M, L, XL, Sur-Mesure", badge: "", image: "", featured: false };

const ProduitsTab = ({ products, categories, reload }) => {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setForm({ ...form, image: process.env.REACT_APP_BACKEND_URL + data.url });
      toast.success("Photo envoyée.");
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Envoi impossible.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      name: form.name,
      category: form.category,
      sub: form.sub || null,
      description: form.description,
      fabric: form.fabric,
      price_xof: parseInt(form.price_xof, 10),
      sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      badge: form.badge || null,
      image: form.image,
      featured: form.featured,
    };
    try {
      if (form.id) await api.patch(`/admin/products/${form.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(form.id ? "Pièce mise à jour." : "Pièce ajoutée à la boutique.");
      setForm(null);
      reload();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Enregistrement impossible.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer « ${p.name} » de la boutique ?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Pièce supprimée.");
      reload();
    } catch {
      toast.error("Suppression impossible.");
    }
  };

  const edit = (p) => setForm({
    id: p.id, name: p.name, category: p.category, sub: p.sub || "", description: p.description,
    fabric: p.fabric, price_xof: String(p.price_xof), sizes: (p.sizes || []).join(", "),
    badge: p.badge || "", image: p.image, featured: !!p.featured,
  });

  return (
    <div className="mt-10">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-or">Catalogue — {products.length} pièces</p>
        {!form && (
          <button
            data-testid="admin-new-product-button"
            onClick={() => setForm({ ...EMPTY_PRODUCT })}
            className="flex items-center gap-2 border border-or/60 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-or transition-all hover:bg-or hover:text-noir"
          >
            <Plus size={13} /> Nouvelle pièce
          </button>
        )}
      </div>

      {form && (
        <form data-testid="admin-product-form" onSubmit={save} className="gold-frame mt-6 grid gap-5 border border-ligne bg-card2 p-8 lg:grid-cols-2">
          <div className="flex items-center justify-between lg:col-span-2">
            <p className="font-serif text-xl text-ivoire">{form.id ? "Modifier la pièce" : "Nouvelle pièce"}</p>
            <button type="button" data-testid="product-form-close" onClick={() => setForm(null)} className="text-argile hover:text-or" aria-label="Fermer le formulaire"><X size={18} /></button>
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">Nom de la pièce</label>
            <input data-testid="product-form-name" required value={form.name} onChange={set("name")} className="w-full border border-ligne bg-elevated px-4 py-3 text-sm text-ivoire focus:border-or" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">Catégorie</label>
              <select data-testid="product-form-category" value={form.category} onChange={set("category")} className="w-full border border-ligne bg-elevated px-4 py-3 text-sm text-ivoire focus:border-or">
                {(categories.length ? categories : CATEGORIES).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">Sous-famille</label>
              <input data-testid="product-form-sub" value={form.sub} onChange={set("sub")} placeholder="polos, robes de mariée…" className="w-full border border-ligne bg-elevated px-4 py-3 text-sm text-ivoire focus:border-or" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">Prix (FCFA)</label>
              <input data-testid="product-form-price" required type="number" min="1000" value={form.price_xof} onChange={set("price_xof")} className="w-full border border-ligne bg-elevated px-4 py-3 text-sm text-ivoire focus:border-or" />
            </div>
            <div>
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">Étiquette (facultatif)</label>
              <input data-testid="product-form-badge" value={form.badge} onChange={set("badge")} placeholder="Nouveau, Iconique…" className="w-full border border-ligne bg-elevated px-4 py-3 text-sm text-ivoire focus:border-or" />
            </div>
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">Tailles (séparées par des virgules)</label>
            <input data-testid="product-form-sizes" value={form.sizes} onChange={set("sizes")} className="w-full border border-ligne bg-elevated px-4 py-3 text-sm text-ivoire focus:border-or" />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">Matière</label>
            <input data-testid="product-form-fabric" value={form.fabric} onChange={set("fabric")} className="w-full border border-ligne bg-elevated px-4 py-3 text-sm text-ivoire focus:border-or" />
          </div>
          <div className="lg:col-span-2">
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">Description</label>
            <textarea data-testid="product-form-description" rows="3" value={form.description} onChange={set("description")} className="w-full border border-ligne bg-elevated px-4 py-3 text-sm text-ivoire focus:border-or" />
          </div>
          <div className="lg:col-span-2">
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">Photo de la pièce</label>
            <div className="flex items-center gap-5">
              {form.image && <img src={form.image} alt="aperçu" className="h-24 w-20 border border-ligne object-cover" />}
              <input ref={fileRef} data-testid="admin-upload-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} className="hidden" />
              <button type="button" data-testid="admin-upload-button" onClick={() => fileRef.current?.click()} disabled={busy} className="flex items-center gap-2 border border-ligne px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-argile transition-all hover:border-or hover:text-or disabled:opacity-50">
                <ImagePlus size={14} /> {busy ? "Envoi…" : "Téléverser une photo"}
              </button>
              <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-argile">
                <input type="checkbox" checked={form.featured} onChange={set("featured")} className="accent-[#F7F5F0]" /> Pièce vedette
              </label>
            </div>
          </div>
          <button data-testid="product-save-button" type="submit" disabled={busy} className="bg-or py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-noir transition-all hover:bg-orclair disabled:opacity-50 lg:col-span-2">
            {busy ? "Enregistrement…" : form.id ? "Mettre à jour" : "Publier dans la boutique"}
          </button>
        </form>
      )}

      <div className="mt-8 overflow-x-auto">
        <table className="w-full min-w-[720px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-ligne text-left font-mono text-[9px] uppercase tracking-[0.2em] text-argile">
              <th className="py-4 pr-4">Pièce</th><th className="py-4 pr-4">Catégorie</th>
              <th className="py-4 pr-4">Sous-famille</th><th className="py-4 pr-4">Prix</th>
              <th className="py-4 pr-4">Vedette</th><th className="py-4 pr-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} data-testid={`product-row-${p.slug}`} className="border-b border-ligne/60">
                <td className="py-3 pr-4">
                  <div className="flex items-center gap-3">
                    <img src={p.image} alt="" className="h-14 w-11 border border-ligne object-cover" />
                    <span className="text-ivoire">{p.name}</span>
                  </div>
                </td>
                <td className="py-3 pr-4 text-xs uppercase tracking-wider text-argile">{p.category}</td>
                <td className="py-3 pr-4 text-xs text-argile">{p.sub || "—"}</td>
                <td className="py-3 pr-4 text-or">{fmtXOF(p.price_xof)}</td>
                <td className="py-3 pr-4">{p.featured ? <CheckCircle2 size={15} className="text-or" /> : <span className="text-argile">—</span>}</td>
                <td className="py-3 pr-4 text-right">
                  <button data-testid={`product-edit-${p.slug}`} onClick={() => edit(p)} className="mr-3 text-argile transition-colors hover:text-or" aria-label={`Modifier ${p.name}`}><Pencil size={15} /></button>
                  <button data-testid={`product-delete-${p.slug}`} onClick={() => remove(p)} className="text-argile transition-colors hover:text-or" aria-label={`Supprimer ${p.name}`}><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CategoriesTab = ({ categories, reload }) => {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setForm({ ...form, image: process.env.REACT_APP_BACKEND_URL + data.url });
      toast.success("Photo envoyée.");
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Envoi impossible.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { label: form.label, tagline: form.tagline || "", sort: Number(form.sort) || 99, active: !!form.active };
    try {
      if (form.key) await api.patch(`/admin/categories/${form.key}`, payload);
      else await api.post("/admin/categories", payload);
      toast.success(form.key ? "Catégorie mise à jour." : "Catégorie créée.");
      setForm(null);
      reload();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Enregistrement impossible.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer la catégorie « ${c.label} » ?`)) return;
    try {
      await api.delete(`/admin/categories/${c.key}`);
      toast.success("Catégorie supprimée.");
      setForm(null);
      reload();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Suppression impossible.");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-or">Catégories de la boutique</p>
        {!form && (
          <button data-testid="category-add-button" onClick={() => setForm({ label: "", tagline: "", sort: 99, active: true })} className="flex items-center gap-2 border border-or/60 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-or transition-all hover:bg-or hover:text-noir">
            <Plus size={13} /> Nouvelle catégorie
          </button>
        )}
      </div>

      {form && (
        <form data-testid="category-form" onSubmit={save} className="mt-6 grid gap-4 border border-ligne bg-card2 p-6 lg:grid-cols-2">
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">Libellé {form.key ? "" : "(la clé est créée automatiquement)"}</label>
            <input data-testid="category-form-label" required value={form.label} onChange={set("label")} placeholder="Accessoires, Éditions limitées…" className="w-full border border-ligne bg-elevated px-4 py-3 text-sm text-ivoire focus:border-or" />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">Ordre d'affichage</label>
            <input data-testid="category-form-sort" type="number" value={form.sort} onChange={set("sort")} className="w-full border border-ligne bg-elevated px-4 py-3 text-sm text-ivoire focus:border-or" />
          </div>
          <div className="lg:col-span-2">
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">Accroche (facultatif)</label>
            <input data-testid="category-form-tagline" value={form.tagline || ""} onChange={set("tagline")} placeholder="Pièces, accessoires…" className="w-full border border-ligne bg-elevated px-4 py-3 text-sm text-ivoire focus:border-or" />
          </div>
          <div className="lg:col-span-2">
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">Visuel de la catégorie</label>
            <div className="flex items-center gap-5">
              {form.image && <img src={form.image} alt="aperçu" className="h-24 w-20 border border-ligne object-cover" />}
              <input ref={fileRef} data-testid="category-upload-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} className="hidden" />
              <button type="button" data-testid="category-upload-button" onClick={() => fileRef.current?.click()} disabled={busy} className="flex items-center gap-2 border border-ligne px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-argile transition-all hover:border-or hover:text-or disabled:opacity-50">
                <ImagePlus size={14} /> {busy ? "Envoi…" : "Téléverser un visuel"}
              </button>
              <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-argile">
                <input type="checkbox" checked={!!form.active} onChange={set("active")} className="accent-[#F7F5F0]" /> Catégorie visible
              </label>
            </div>
          </div>
          <button data-testid="category-save-button" type="submit" disabled={busy} className="bg-or py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-noir transition-all hover:bg-orclair disabled:opacity-50 lg:col-span-2">
            {busy ? "Enregistrement…" : form.key ? "Mettre à jour" : "Créer la catégorie"}
          </button>
        </form>
      )}

      <div className="mt-8 overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-ligne text-left font-mono text-[9px] uppercase tracking-[0.2em] text-argile">
              <th className="py-4 pr-4">Catégorie</th><th className="py-4 pr-4">Clé</th>
              <th className="py-4 pr-4">Pièces</th><th className="py-4 pr-4">Ordre</th>
              <th className="py-4 pr-4">Visible</th><th className="py-4 pr-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((c) => (
              <tr key={c.key} data-testid={`category-row-${c.key}`} className="border-b border-ligne/60">
                <td className="py-3 pr-4">
                  <div className="flex items-center gap-3">
                    {c.image && <img src={c.image} alt="" className="h-12 w-10 border border-ligne object-cover" />}
                    <span className="text-ivoire">{c.label}</span>
                  </div>
                </td>
                <td className="py-3 pr-4 font-mono text-xs text-argile">{c.key}</td>
                <td className="py-3 pr-4 text-or">{c.count}</td>
                <td className="py-3 pr-4 text-argile">{c.sort}</td>
                <td className="py-3 pr-4">{c.active ? <CheckCircle2 size={15} className="text-or" /> : <span className="text-argile">Masquée</span>}</td>
                <td className="py-3 pr-4 text-right">
                  <button data-testid={`category-edit-${c.key}`} onClick={() => setForm({ ...c, active: !!c.active })} className="mr-3 text-argile transition-colors hover:text-or" aria-label={`Modifier ${c.label}`}><Pencil size={15} /></button>
                  <button data-testid={`category-delete-${c.key}`} onClick={() => remove(c)} className="text-argile transition-colors hover:text-or" aria-label={`Supprimer ${c.label}`}><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PaiementsTab = () => {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setCfg({ ...cfg, [k]: e.target.value });

  useEffect(() => {
    api.get("/admin/payment-settings").then((r) => setCfg(r.data)).catch(() => toast.error("Chargement impossible."));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/payment-settings", {
        nicepay_token: cfg.nicepay_token || "",
        nicepay_account: cfg.nicepay_account || "",
        stripe_key: cfg.stripe_key || "",
        stripe_currency: cfg.stripe_currency || "eur",
      });
      setCfg(data);
      toast.success("Paramètres de paiement enregistrés.", { description: "Les nouveaux paiements utilisent immédiatement ces clés." });
    } catch {
      toast.error("Enregistrement impossible.");
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) return <p className="py-16 text-center font-mono text-[11px] uppercase tracking-[0.3em] text-argile">Chargement…</p>;

  return (
    <form data-testid="admin-payments-form" onSubmit={save} className="mt-10 max-w-2xl space-y-8">
      <div className="gold-frame border border-ligne bg-card2 p-8">
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-2 font-serif text-xl text-ivoire"><KeyRound size={17} className="text-or" /> NicePay — Mobile Money</p>
          <span data-testid="nicepay-status-chip" className={`px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] ${cfg.nicepay_ready ? "bg-or/15 text-or" : "border border-ligne text-argile"}`}>
            {cfg.nicepay_ready ? "Actif — vrais encaissements" : "Mode simulation"}
          </span>
        </div>
        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">Jeton API marchand</label>
            <input data-testid="payment-input-nicepay-token" type="password" value={cfg.nicepay_token} onChange={set("nicepay_token")} placeholder="collez votre jeton nicepay…" className="w-full border border-ligne bg-elevated px-4 py-3 text-sm text-ivoire focus:border-or" />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">Account ID marchand</label>
            <input data-testid="payment-input-nicepay-account" value={cfg.nicepay_account} onChange={set("nicepay_account")} placeholder="identifiant marchand" className="w-full border border-ligne bg-elevated px-4 py-3 text-sm text-ivoire focus:border-or" />
          </div>
        </div>
        <p className="mt-4 text-xs leading-relaxed text-argile">
          Collez ici les identifiants de votre compte marchand nicepaygroup.com : les encaissements
          Wave, Orange Money, MTN MoMo et Moov deviennent réels immédiatement.
        </p>
      </div>

      <div className="gold-frame border border-ligne bg-card2 p-8">
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-2 font-serif text-xl text-ivoire"><CreditCard size={17} className="text-or" /> Stripe — Carte bancaire</p>
          <span data-testid="stripe-status-chip" className={`px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] ${cfg.stripe_ready ? "bg-or/15 text-or" : "border border-ligne text-argile"}`}>
            {cfg.stripe_ready ? "Actif — paiement par carte" : "Non configuré"}
          </span>
        </div>
        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">Clé secrète Stripe (sk_test_… ou sk_live_…)</label>
            <input data-testid="payment-input-stripe-key" type="password" value={cfg.stripe_key} onChange={set("stripe_key")} placeholder="sk_test_…" className="w-full border border-ligne bg-elevated px-4 py-3 text-sm text-ivoire focus:border-or" />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">Devise de facturation</label>
            <select data-testid="payment-input-stripe-currency" value={cfg.stripe_currency} onChange={set("stripe_currency")} className="w-full border border-ligne bg-elevated px-4 py-3 text-sm text-ivoire focus:border-or">
              <option value="eur">Euro (€) — parité fixe FCFA</option>
              <option value="usd">Dollar US ($)</option>
            </select>
          </div>
        </div>
        <p className="mt-4 text-xs leading-relaxed text-argile">
          Votre clé Stripe (compte enregistré dans un pays supporté) active le paiement par carte
          au checkout. Montants convertis du FCFA à parité fixe (1 € = 655,957 FCFA). Webhook à
          déclarer chez Stripe : {window.location.origin}/api/webhook/stripe
        </p>
      </div>

      <button data-testid="payment-save-button" type="submit" disabled={busy} className="w-full bg-or py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-noir transition-all hover:bg-orclair disabled:opacity-50">
        {busy ? "Enregistrement…" : "Enregistrer les paramètres"}
      </button>
    </form>
  );
};

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  const loadAll = async () => {
    try {
      const [s, o, a, n, p, c] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/orders"),
        api.get("/admin/appointments"), api.get("/admin/newsletter"),
        api.get("/products"), api.get("/admin/categories"),
      ]);
      setStats(s.data); setOrders(o.data); setAppointments(a.data); setSubscribers(n.data); setProducts(p.data); setCategories(c.data);
    } catch {
      toast.error("Chargement des données impossible.");
    }
  };

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) loadAll();
  }, [user]);

  const setOrderStatus = async (ref, status) => {
    try {
      const { data } = await api.patch(`/admin/orders/${ref}`, { status });
      setOrders((prev) => prev.map((o) => (o.ref === ref ? data : o)));
      toast.success(`Commande ${ref} → ${ORDER_STATUS.find((s) => s.key === status)?.label}`);
      loadAll();
    } catch {
      toast.error("Mise à jour impossible.");
    }
  };

  const setRdvStatus = async (ref, status) => {
    try {
      const { data } = await api.patch(`/admin/appointments/${ref}`, { status });
      setAppointments((prev) => prev.map((a) => (a.ref === ref ? data : a)));
      toast.success(`Rendez-vous ${ref} mis à jour`);
    } catch {
      toast.error("Mise à jour impossible.");
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  if (user === undefined)
    return <main className="min-h-screen pt-40 text-center font-mono text-[11px] uppercase tracking-[0.3em] text-argile">Vérification de la session…</main>;

  if (!user) return <LoginScreen onLogin={setUser} />;

  const TABS = [
    { key: "stats", label: "Tableau de bord", icon: BarChart3 },
    { key: "commandes", label: `Commandes (${orders.length})`, icon: PackageCheck },
    { key: "rendezvous", label: `Rendez-vous (${appointments.length})`, icon: CalendarCheck },
    { key: "produits", label: `Catalogue (${products.length})`, icon: ImagePlus },
    { key: "categories", label: `Catégories (${categories.length})`, icon: PackageCheck },
    { key: "abonnes", label: `Abonnés (${subscribers.length})`, icon: Users },
    { key: "paiements", label: "Paiements", icon: CreditCard },
  ];

  return (
    <main data-testid="admin-dashboard" className="mx-auto max-w-[1500px] px-6 pb-24 pt-28 lg:px-12 lg:pt-36">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-ligne pb-8">
        <div className="flex items-center gap-4">
          <LogoMark size={40} />
          <div>
            <h1 className="font-serif text-2xl tracking-[0.12em] text-ivoire">ESPACE DIRECTION</h1>
            <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.3em] text-or">{user.email}</p>
          </div>
        </div>
        <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 border border-ligne px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-argile transition-all hover:border-or hover:text-or">
          <LogOut size={13} /> Quitter
        </button>
      </div>

      <nav className="mt-8 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-all ${
              tab === t.key ? "border-or bg-or text-noir" : "border-ligne text-argile hover:border-or hover:text-or"
            }`}
          >
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </nav>

      {tab === "stats" && stats && (
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          <StatCard testid="admin-stat-commandes" icon={PackageCheck} label="Commandes" value={stats.orders} sub={`${stats.orders_pending} en attente`} />
          <StatCard testid="admin-stat-ca" icon={CheckCircle2} label="CA encaissé" value={fmtXOF(stats.revenue_xof)} sub={fmtEUR(stats.revenue_xof)} />
          <StatCard testid="admin-stat-payees" icon={Clock} label="Payées" value={stats.orders_paid} />
          <StatCard testid="admin-stat-rdv" icon={CalendarCheck} label="Rendez-vous" value={stats.appointments} />
          <StatCard testid="admin-stat-abonnes" icon={Mail} label="Abonnés Cercle" value={stats.subscribers} />
        </div>
      )}

      {tab === "commandes" && (
        <div className="mt-10 overflow-x-auto" data-testid="admin-orders-table">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-or">Commandes — {orders.length}</p>
            <a
              data-testid="admin-export-button"
              href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/export`}
              download
              className="flex items-center gap-2 border border-or/60 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-or transition-all hover:bg-or hover:text-noir"
            >
              <Download size={13} /> Exporter Excel
            </a>
          </div>
          <table className="w-full min-w-[760px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-ligne text-left font-mono text-[9px] uppercase tracking-[0.2em] text-argile">
                <th className="py-4 pr-4">Réf.</th><th className="py-4 pr-4">Client</th>
                <th className="py-4 pr-4">Pièces</th><th className="py-4 pr-4">Paiement</th>
                <th className="py-4 pr-4">Total</th><th className="py-4 pr-4">Date</th>
                <th className="py-4 pr-4">Statut</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.ref} data-testid={`order-row-${o.ref}`} className="border-b border-ligne/60 align-top">
                  <td className="py-4 pr-4 font-mono text-xs text-or">{o.ref}</td>
                  <td className="py-4 pr-4">
                    <p className="text-ivoire">{o.customer.full_name}</p>
                    <p className="text-xs text-argile">{o.customer.phone} · {o.customer.city}</p>
                  </td>
                  <td className="py-4 pr-4 text-xs text-argile">
                    {o.items.map((i, k) => <p key={k}>{i.name} × {i.quantity}</p>)}
                  </td>
                  <td className="py-4 pr-4 text-xs">
                    <p className="text-argile">{o.payment_method === "mobile_money" ? `Mobile Money · ${o.operator}` : o.payment_method === "carte" ? "Carte (Stripe)" : "À la livraison"}</p>
                    <p className={o.payment_status === "payee" ? "text-or" : "text-argile"}>{o.payment_status === "payee" ? "Payé" : "Non payé"}</p>
                  </td>
                  <td className="py-4 pr-4 text-ivoire">{fmtXOF(o.total_xof)}</td>
                  <td className="py-4 pr-4 text-xs text-argile">{fmtDate(o.created_at)}</td>
                  <td className="py-4 pr-4">
                    <select data-testid={`order-status-select-${o.ref}`} value={o.status} onChange={(e) => setOrderStatus(o.ref, e.target.value)} className="border border-ligne bg-card2 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-ivoire focus:border-or">
                      {ORDER_STATUS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {orders.length === 0 && <p className="py-16 text-center text-sm text-argile">Aucune commande pour l'instant.</p>}
        </div>
      )}

      {tab === "rendezvous" && (
        <div className="mt-10 space-y-4" data-testid="admin-appointments-list">
          {appointments.map((a) => (
            <div key={a.ref} data-testid={`rdv-row-${a.ref}`} className="gold-frame flex flex-wrap items-center justify-between gap-4 border border-ligne bg-card2 p-5">
              <div>
                <p className="font-serif text-lg text-ivoire">{a.full_name} <span className="ml-2 font-mono text-[10px] tracking-[0.2em] text-or">{a.ref}</span></p>
                <p className="mt-1 text-xs text-argile">{a.service} · souhaité le {a.preferred_date} · {a.phone} · {a.email}</p>
                {a.message && <p className="mt-2 max-w-xl text-xs italic text-argile">« {a.message} »</p>}
              </div>
              <div className="flex items-center gap-3">
                <StatusPill status={a.status} />
                <select data-testid={`rdv-status-select-${a.ref}`} value={a.status} onChange={(e) => setRdvStatus(a.ref, e.target.value)} className="border border-ligne bg-elevated px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-ivoire focus:border-or">
                  {RDV_STATUS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
            </div>
          ))}
          {appointments.length === 0 && <p className="py-16 text-center text-sm text-argile">Aucune demande de rendez-vous.</p>}
        </div>
      )}

      {tab === "produits" && <ProduitsTab products={products} categories={categories} reload={loadAll} />}

      {tab === "categories" && <CategoriesTab categories={categories} reload={loadAll} />}

      {tab === "abonnes" && (
        <div className="mt-10" data-testid="admin-subscribers-list">
          <div className="gold-frame divide-y divide-ligne border border-ligne bg-card2">
            {subscribers.map((s, i) => (
              <div key={i} data-testid={`subscriber-row-${i}`} className="flex items-center gap-4 px-6 py-4">
                <Mail size={15} className="text-or" />
                <p className="text-sm text-ivoire">{s.email}</p>
                <p className="ml-auto font-mono text-[10px] uppercase tracking-[0.2em] text-argile">{fmtDate(s.created_at)}</p>
              </div>
            ))}
            {subscribers.length === 0 && <p className="py-16 text-center text-sm text-argile">Aucun abonné au Cercle B.KOSS.</p>}
          </div>
        </div>
      )}

      {tab === "paiements" && <PaiementsTab />}
    </main>
  );
}
